import React from 'react';
import { supabase } from '../lib/supabase';
import MilestoneCompletionDetectionService from './MilestoneCompletionDetectionService';
import { toast } from 'react-hot-toast';

class MilestoneCompletionTriggers {
  static subscriptions = new Map();
  static notificationQueue = [];
  static isProcessing = false;

  static async initialize(userId) {
    if (!userId) return;
    
    this.cleanup();
    
    // Monitor college_journey_colleges changes
    const collegesSub = supabase
      .channel(`colleges-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'college_journey_colleges',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        this.handleCollegeChange(userId, payload);
      })
      .subscribe();
    
    // Monitor college_essays changes
    const essaysSub = supabase
      .channel(`essays-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'college_essays',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        this.handleEssayChange(userId, payload);
      })
      .subscribe();
    
    // Monitor college_applications changes
    const applicationsSub = supabase
      .channel(`applications-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'college_applications',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        this.handleApplicationChange(userId, payload);
      })
      .subscribe();
    
    // Monitor timeline_items changes (for subtask completions)
    const timelineSub = supabase
      .channel(`timeline-${userId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'timeline_items',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        this.handleTimelineChange(userId, payload);
      })
      .subscribe();
    
    // Monitor form submissions (FAFSA, CSS)
    const formsSub = supabase
      .channel(`forms-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'financial_aid_forms',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        this.handleFormChange(userId, payload);
      })
      .subscribe();
    
    // Store subscriptions for cleanup
    this.subscriptions.set(`colleges-${userId}`, collegesSub);
    this.subscriptions.set(`essays-${userId}`, essaysSub);
    this.subscriptions.set(`applications-${userId}`, applicationsSub);
    this.subscriptions.set(`timeline-${userId}`, timelineSub);
    this.subscriptions.set(`forms-${userId}`, formsSub);
  }
  
  static cleanup() {
    for (const [key, subscription] of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions.clear();
  }
  
  static async handleCollegeChange(userId, payload) {
    const changeType = payload.eventType;
    const data = payload.new || payload.old;
    
    // Check relevant milestones based on change
    const relevantRules = [];
    
    if (changeType === 'INSERT') {
      const category = data.category;
      if (category === 'reach') {
        relevantRules.push('select_reach_schools');
      } else if (category === 'match') {
        relevantRules.push('select_match_schools');
      } else if (category === 'safety') {
        relevantRules.push('select_safety_schools');
      }
      relevantRules.push('build_school_list');
    }
    
    if (changeType === 'UPDATE' && data.decision_status) {
      relevantRules.push('track_decisions');
    }
    
    await this.checkMilestones(userId, relevantRules, data.college_id);
  }
  
  static async handleEssayChange(userId, payload) {
    const changeType = payload.eventType;
    const data = payload.new || payload.old;
    
    const relevantRules = [];
    
    if (changeType === 'UPDATE') {
      const oldData = payload.old;
      
      // Check if draft was completed
      if (oldData?.draft_version < data.draft_version) {
        relevantRules.push('draft_personal_statement', 'draft_essays');
      }
      
      // Check if essay was finalized
      if (!oldData?.is_final && data.is_final) {
        relevantRules.push('finalize_essays', 'review_essays');
      }
    }
    
    await this.checkMilestones(userId, relevantRules, data.college_id);
  }
  
  static async handleApplicationChange(userId, payload) {
    const data = payload.new || payload.old;
    const relevantRules = [];
    
    if (payload.eventType === 'UPDATE') {
      const oldData = payload.old;
      
      // Check if application was submitted
      if (!oldData?.submitted && data.submitted) {
        relevantRules.push('submit_applications', 'submit_early_apps');
      }
      
      // Check if transcript was requested
      if (!oldData?.transcript_requested && data.transcript_requested) {
        relevantRules.push('request_transcripts');
      }
      
      // Check if recommendations were requested
      if (!oldData?.recommendations_requested && data.recommendations_requested) {
        relevantRules.push('request_recommendations');
      }
    }
    
    await this.checkMilestones(userId, relevantRules, data.college_id);
  }
  
  static async handleTimelineChange(userId, payload) {
    const data = payload.new;
    
    // Only process subtask completions
    if (data.source_type !== 'milestone_subtask') return;
    if (data.workflow_phase !== 'complete') return;
    if (!data.parent_id) return;
    
    // Check if all sibling subtasks are complete
    const { data: siblings } = await supabase
      .from('timeline_items')
      .select('*')
      .eq('parent_id', data.parent_id)
      .eq('source_type', 'milestone_subtask');
    
    if (siblings?.every(s => s.workflow_phase === 'complete')) {
      // All subtasks complete - suggest parent completion
      const { data: parent } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('id', data.parent_id)
        .single();
      
      if (parent && parent.workflow_phase !== 'complete') {
        this.queueNotification({
          type: 'subtasks_complete',
          milestone: parent,
          message: `All subtasks complete for "${parent.title}". Mark as complete?`,
          action: async () => {
            await MilestoneCompletionDetectionService.autoCompleteMilestone(
              parent.id,
              userId
            );
          }
        });
      }
    }
  }
  
  static async handleFormChange(userId, payload) {
    const data = payload.new || payload.old;
    const relevantRules = [];
    
    if (data.form_type === 'fafsa' && data.submitted) {
      relevantRules.push('submit_fafsa');
    }
    
    if (data.form_type === 'css_profile' && data.submitted) {
      relevantRules.push('submit_css_profile');
    }
    
    await this.checkMilestones(userId, relevantRules);
  }
  
  static async checkMilestones(userId, ruleNames, collegeId = null) {
    if (!ruleNames.length) return;
    
    // Get milestones that use these rules
    let query = supabase
      .from('timeline_items')
      .select('*')
      .eq('user_id', userId)
      .in('source_type', ['milestone_template', 'milestone'])
      .eq('workflow_phase', 'in_progress')
      .or(ruleNames.map(r => `metadata->completion_rule.eq.${r}`).join(','));
    
    if (collegeId) {
      query = query.eq('college_id', collegeId);
    }
    
    const { data: milestones } = await query;
    
    if (!milestones?.length) return;
    
    // Check each milestone for completion
    for (const milestone of milestones) {
      const suggestion = await MilestoneCompletionDetectionService.checkMilestoneCompletion(
        milestone,
        userId
      );
      
      if (suggestion?.isComplete) {
        this.queueNotification({
          type: 'auto_complete',
          milestone,
          suggestion,
          message: `${suggestion.progress}. Mark "${milestone.title}" as complete?`,
          action: async () => {
            await MilestoneCompletionDetectionService.autoCompleteMilestone(
              milestone.id,
              userId
            );
          }
        });
      }
    }
    
    this.processNotificationQueue();
  }
  
  static queueNotification(notification) {
    // Avoid duplicate notifications
    const isDuplicate = this.notificationQueue.some(n => 
      n.milestone.id === notification.milestone.id &&
      n.type === notification.type
    );
    
    if (!isDuplicate) {
      this.notificationQueue.push(notification);
    }
  }
  
  static async processNotificationQueue() {
    if (this.isProcessing || this.notificationQueue.length === 0) return;
    
    this.isProcessing = true;
    
    // Process notifications in batches
    const batch = this.notificationQueue.splice(0, 3);
    
    for (const notification of batch) {
      // Show toast with action button
      toast((t) => (
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">
              Milestone can be completed
            </p>
            <p className="text-sm text-stone-500">
              {notification.message}
            </p>
          </div>
          <div className="flex gap-2 ml-4">
            <button
              onClick={() => {
                notification.action();
                toast.dismiss(t.id);
                toast.success('Milestone completed!');
              }}
              className="px-3 py-1 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700"
            >
              Complete
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1 text-sm bg-stone-200 text-slate-700 rounded hover:bg-stone-300"
            >
              Dismiss
            </button>
          </div>
        </div>
      ), {
        duration: 10000,
        position: 'bottom-right'
      });
      
      // Small delay between notifications
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    this.isProcessing = false;
    
    // Process remaining notifications
    if (this.notificationQueue.length > 0) {
      setTimeout(() => this.processNotificationQueue(), 2000);
    }
  }
  
  // Manual trigger for testing
  static async checkAllMilestonesForUser(userId) {
    const suggestions = await MilestoneCompletionDetectionService.checkAllMilestones(userId);
    
    for (const suggestion of suggestions) {
      if (suggestion.isComplete) {
        this.queueNotification({
          type: 'manual_check',
          milestone: { id: suggestion.milestoneId, title: suggestion.milestoneTitle },
          suggestion,
          message: `${suggestion.progress}. Mark "${suggestion.milestoneTitle}" as complete?`,
          action: async () => {
            await MilestoneCompletionDetectionService.autoCompleteMilestone(
              suggestion.milestoneId,
              userId
            );
          }
        });
      }
    }
    
    this.processNotificationQueue();
  }
}

export default MilestoneCompletionTriggers;